import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';
import { apiFetch } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { productImageSrc, retryProductImageOnce } from '../utils/productImage';
import {
    DollarSign, TrendingUp, Package, AlertTriangle, Sun, Store, ChevronRight,
    Search, Download, Filter, Box, ShoppingCart, Users,
} from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell,
} from 'recharts';

const RANGES = [
    { id: 'hoy', label: 'Hoy' },
    { id: '7d', label: '7 días' },
    { id: '30d', label: '30 días' },
    { id: '12m', label: '12 meses' },
];

const DONUT_COLORS = ['#F5C400', '#F59E0B', '#FB923C', '#D6D3D1', '#A8A29E'];

const formatCurrency = (amount) => new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
}).format(Number(amount) || 0);

const santiagoToday = () => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
}).format(new Date());

const addDays = (iso, days) => {
    const [year, month, day] = iso.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
};

const sumBetween = (series, from, to) => series.reduce((acc, row) => {
    if (row.date >= from && row.date <= to) {
        acc.amount += Number(row.total_amount) || 0;
        acc.orders += Number(row.total_sales) || 0;
    }
    return acc;
}, { amount: 0, orders: 0 });

const percentDelta = (current, previous) => {
    if (!previous) return current ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
};

const dayLabel = (iso, withMonth = false) => {
    const [year, month, day] = iso.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 15));
    return date.toLocaleDateString('es-CL', {
        weekday: 'short',
        day: 'numeric',
        month: withMonth ? 'long' : undefined,
        timeZone: 'UTC',
    });
};

const monthLabel = (iso) => {
    const [year, month] = iso.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, 1, 15));
    return date.toLocaleDateString('es-CL', { month: 'short', timeZone: 'UTC' });
};

const axisMoney = (value) => {
    const amount = Number(value) || 0;
    if (Math.abs(amount) >= 1000000) {
        return `$${(amount / 1000000).toLocaleString('es-CL', { maximumFractionDigits: 1 })}M`;
    }
    return `$${amount.toLocaleString('es-CL')}`;
};

function SalesTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    return (
        <div className="dash-tip">
            <span>{row.full || row.name}</span>
            <strong>Ventas: {formatCurrency(payload[0].value)}</strong>
        </div>
    );
}

function Delta({ value, suffix, invert = false }) {
    const up = value >= 0;
    const bad = invert ? up && value !== 0 : !up;
    const arrow = up ? '↑' : '↓';
    const text = `${arrow} ${up ? '+' : ''}${value}${suffix}`;
    return <span className={`dash-delta ${bad ? 'is-down' : 'is-up'}`}>{text}</span>;
}

export default function HomePage() {
    const { user } = useAuth();
    const [dash, setDash] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [range, setRange] = useState('30d');
    const [alertQuery, setAlertQuery] = useState('');
    const [alertFilter, setAlertFilter] = useState('todos');

    useEffect(() => {
        apiFetch(`${API_URL}/api/reports/dashboard`)
            .then(async (res) => {
                const data = await res.json().catch(() => null);
                if (res.ok && data) setDash(data);
            })
            .catch(console.error);

        apiFetch(`${API_URL}/api/reports/alerts`)
            .then(async (res) => {
                const data = await res.json().catch(() => null);
                if (res.ok && Array.isArray(data)) setAlerts(data);
            })
            .catch(console.error);
    }, []);

    const today = santiagoToday();
    const series = dash?.series || [];
    const todayStats = sumBetween(series, today, today);
    const yesterdayStats = sumBetween(series, addDays(today, -1), addDays(today, -1));
    const monthStats = sumBetween(series, addDays(today, -29), today);
    const prevMonthStats = sumBetween(series, addDays(today, -59), addDays(today, -30));
    const expiringNow = dash?.expiring_now ?? alerts.length;
    const expiringDelta = expiringNow - Number(dash?.expiring_prev || 0);

    const chartData = useMemo(() => {
        if (range === 'hoy') {
            return (dash?.hours || []).map((row) => ({
                name: `${String(row.hour).padStart(2, '0')}:00`,
                full: `Hoy ${String(row.hour).padStart(2, '0')}:00`,
                sales: Number(row.total_amount) || 0,
            }));
        }
        if (range === '12m') {
            const from = addDays(today, -364);
            const buckets = new Map();
            series.forEach((row) => {
                if (row.date < from || row.date > today) return;
                const key = row.date.slice(0, 7);
                buckets.set(key, (buckets.get(key) || 0) + (Number(row.total_amount) || 0));
            });
            return [...buckets.entries()].map(([key, sales]) => ({
                name: monthLabel(`${key}-01`),
                full: monthLabel(`${key}-01`),
                sales,
            }));
        }
        const days = range === '7d' ? 6 : 29;
        const from = addDays(today, -days);
        return series
            .filter((row) => row.date >= from && row.date <= today)
            .map((row) => ({
                name: dayLabel(row.date),
                full: dayLabel(row.date, true),
                sales: Number(row.total_amount) || 0,
            }));
    }, [dash, range, series, today]);

    const categories = dash?.categories || [];
    const categoryTotal = categories.reduce((sum, row) => sum + Number(row.total || 0), 0);

    const visibleAlerts = alerts.filter((item) => {
        const days = Number(item.dias_restantes);
        const q = alertQuery.trim().toLowerCase();
        const matchesQuery = !q
            || (item.nombre || '').toLowerCase().includes(q)
            || String(item.codigo_barra || '').includes(q);
        const tone = days <= 2 ? 'urgente' : days <= 8 ? 'por-vencer' : 'proximo';
        const matchesFilter = alertFilter === 'todos' || alertFilter === tone;
        return matchesQuery && matchesFilter;
    }).slice(0, 6);

    const exportAlerts = () => {
        const lines = ['Producto,Codigo,Cantidad,Vence,Dias'];
        visibleAlerts.forEach((item) => {
            lines.push([
                `"${(item.nombre || '').replace(/"/g, '""')}"`,
                item.codigo_barra || '',
                item.cantidad_total || 0,
                item.fecha_vencimiento ? String(item.fecha_vencimiento).slice(0, 10) : '',
                item.dias_restantes ?? '',
            ].join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'alertas-vencimiento.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="dash">
            <div className="dash-hero">
                <div>
                    <h1>Hola, {user?.name || 'Admin'} <Sun size={22} className="dash-sun" aria-hidden /></h1>
                    <p>Aquí tienes un resumen de tu negocio hoy.</p>
                </div>
                <aside className="dash-quote">
                    <div>
                        <p>“Grandes negocios nacen de un gran control”</p>
                    </div>
                    <span className="dash-quote-icon" aria-hidden><Store size={18} /></span>
                </aside>
            </div>

            <section className="dash-stats">
                <article className="dash-stat">
                    <span className="dash-stat-icon is-gold"><DollarSign size={18} /></span>
                    <div>
                        <p>Ventas de Hoy</p>
                        <strong>{formatCurrency(dash?.today_amount ?? todayStats.amount)}</strong>
                        <Delta value={percentDelta(dash?.today_amount ?? todayStats.amount, dash?.yesterday_same_time ?? yesterdayStats.amount)} suffix="% vs. ayer" />
                    </div>
                </article>
                <article className="dash-stat">
                    <span className="dash-stat-icon is-green"><TrendingUp size={18} /></span>
                    <div>
                        <p>Ingresos (30d)</p>
                        <strong>{formatCurrency(monthStats.amount)}</strong>
                        <Delta value={percentDelta(monthStats.amount, prevMonthStats.amount)} suffix="% vs. período anterior" />
                    </div>
                </article>
                <article className="dash-stat">
                    <span className="dash-stat-icon is-blue"><Package size={18} /></span>
                    <div>
                        <p>Total Órdenes (30d)</p>
                        <strong>{monthStats.orders.toLocaleString('es-CL')}</strong>
                        <Delta value={percentDelta(monthStats.orders, prevMonthStats.orders)} suffix="% vs. período anterior" />
                    </div>
                </article>
                <article className="dash-stat">
                    <span className="dash-stat-icon is-rose"><AlertTriangle size={18} /></span>
                    <div>
                        <p>Productos por vencer</p>
                        <strong>{expiringNow.toLocaleString('es-CL')}</strong>
                        <Delta value={expiringDelta} suffix=" vs. mes anterior" invert />
                    </div>
                </article>
            </section>

            <section className="dash-mid">
                <article className="dash-panel">
                    <header className="dash-panel-head">
                        <div>
                            <h2>Resumen de Ventas</h2>
                            <p>Evolución de tus ventas en el tiempo</p>
                        </div>
                        <div className="dash-ranges" role="tablist">
                            {RANGES.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={range === item.id}
                                    className={range === item.id ? 'is-on' : ''}
                                    onClick={() => setRange(item.id)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </header>
                    <div className="dash-chart">
                        {chartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="dashSales" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#F5C400" stopOpacity={0.45} />
                                            <stop offset="100%" stopColor="#F5C400" stopOpacity={0.02} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0ece6" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#a8a29e', fontSize: 11 }} dy={8} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#a8a29e', fontSize: 11 }} tickFormatter={axisMoney} width={72} />
                                    <Tooltip content={<SalesTooltip />} />
                                    <Area type="monotone" dataKey="sales" stroke="#F5B700" strokeWidth={3} fill="url(#dashSales)" activeDot={{ r: 5, fill: '#F5B700', stroke: '#fff', strokeWidth: 2 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="dash-empty">No hay ventas en este período.</p>
                        )}
                    </div>
                </article>

                <article className="dash-panel">
                    <header className="dash-panel-head">
                        <div>
                            <h2>Ventas por categoría</h2>
                            <p>Distribución de ingresos (30 días)</p>
                        </div>
                    </header>
                    {categories.length === 0 ? (
                        <p className="dash-empty">Sin ventas por categoría.</p>
                    ) : (
                        <div className="dash-donut-wrap">
                            <div className="dash-donut">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={categories} dataKey="total" nameKey="nombre" innerRadius={62} outerRadius={84} paddingAngle={2} stroke="none">
                                            {categories.map((row, index) => (
                                                <Cell key={row.nombre} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="dash-donut-center">
                                    <strong>{axisMoney(monthStats.amount || categoryTotal)}</strong>
                                    <span>Total</span>
                                </div>
                            </div>
                            <ul className="dash-legend">
                                {categories.map((row, index) => {
                                    const pct = categoryTotal ? Math.round((Number(row.total) / categoryTotal) * 100) : 0;
                                    return (
                                        <li key={row.nombre}>
                                            <i style={{ background: DONUT_COLORS[index % DONUT_COLORS.length] }} />
                                            <span>{row.nombre}</span>
                                            <b>{pct}%</b>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </article>
            </section>

            <section className="dash-links">
                <Link to="/stock" className="dash-link">
                    <span className="dash-stat-icon is-amber"><Box size={18} /></span>
                    <span>
                        <small>Stock bajo</small>
                        <strong>{Number(dash?.low_stock || 0).toLocaleString('es-CL')} productos</strong>
                    </span>
                    <ChevronRight size={18} />
                </Link>
                <Link to="/precios-pendientes" className="dash-link">
                    <span className="dash-stat-icon is-amber"><ShoppingCart size={18} /></span>
                    <span>
                        <small>Compras pendientes</small>
                        <strong>{Number(dash?.pending_purchases || 0).toLocaleString('es-CL')} órdenes</strong>
                    </span>
                    <ChevronRight size={18} />
                </Link>
                <Link to="/proveedores" className="dash-link">
                    <span className="dash-stat-icon is-amber"><Users size={18} /></span>
                    <span>
                        <small>Proveedores críticos</small>
                        <strong>{Number(dash?.critical_providers || 0).toLocaleString('es-CL')} proveedores</strong>
                    </span>
                    <ChevronRight size={18} />
                </Link>
            </section>

            <section className="dash-panel">
                <header className="dash-alerts-head">
                    <div className="dash-alerts-title">
                        <AlertTriangle size={18} />
                        <div>
                            <h2>Alertas de Vencimiento</h2>
                            <p>Productos que vencen en los próximos 30 días</p>
                        </div>
                    </div>
                    <div className="dash-alerts-tools">
                        <label className="dash-search">
                            <Search size={15} aria-hidden />
                            <input
                                value={alertQuery}
                                onChange={(e) => setAlertQuery(e.target.value)}
                                placeholder="Buscar producto..."
                            />
                        </label>
                        <label className="dash-filter">
                            <Filter size={15} aria-hidden />
                            <select value={alertFilter} onChange={(e) => setAlertFilter(e.target.value)}>
                                <option value="todos">Filtrar</option>
                                <option value="urgente">Urgente</option>
                                <option value="por-vencer">Por vencer</option>
                                <option value="proximo">Próximo</option>
                            </select>
                        </label>
                        <button type="button" className="dash-ghost" onClick={exportAlerts}>
                            <Download size={15} /> Exportar
                        </button>
                        <Link to="/vencimientos" className="dash-all">Ver todos</Link>
                    </div>
                </header>

                {visibleAlerts.length === 0 ? (
                    <p className="dash-empty">No hay alertas con ese filtro.</p>
                ) : (
                    <div className="dash-table-wrap">
                        <table className="dash-table">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th>Código</th>
                                    <th>Cantidad</th>
                                    <th>Vence el</th>
                                    <th>Días Restantes</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleAlerts.map((item) => {
                                    const parsedDays = Number(item.dias_restantes);
                                    const days = Number.isFinite(parsedDays)
                                        ? Math.ceil(parsedDays)
                                        : Math.ceil((new Date(item.fecha_vencimiento) - new Date()) / 86400000);
                                    const tone = days <= 2 ? 'urgente' : days <= 8 ? 'por-vencer' : 'proximo';
                                    const label = tone === 'urgente' ? 'Urgente' : tone === 'por-vencer' ? 'Por vencer' : 'Próximo';
                                    const when = item.fecha_vencimiento
                                        ? new Date(item.fecha_vencimiento).toLocaleDateString('es-CL', { day: 'numeric', month: 'numeric', year: 'numeric' })
                                        : '—';
                                    const photo = productImageSrc(item.foto_url);
                                    return (
                                        <tr key={item.id}>
                                            <td>
                                                <div className="dash-product">
                                                    {photo ? <img src={photo} alt="" onError={retryProductImageOnce} /> : <span />}
                                                    <strong>{item.nombre}</strong>
                                                </div>
                                            </td>
                                            <td className="dash-code">{item.codigo_barra || '—'}</td>
                                            <td className="dash-qty">{Number(item.cantidad_total || 0).toLocaleString('es-CL')} unidades</td>
                                            <td>{when}</td>
                                            <td className={`dash-days is-${tone}`}>{days} días</td>
                                            <td><span className={`dash-pill is-${tone}`}>{label}</span></td>
                                            <td><Link to="/vencimientos">Ver</Link></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
